// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { updateClient, deleteClient, getAllConsultants, getBuilderStatus, requestAgentBridgeUrl, sendClientPasswordReset } from '../../services/api';
import {
    Save, Trash2, Loader2, AlertTriangle, User, Mail, Phone,
    Globe, UserCog, Shield, ShieldOff, Power, Bot, CheckCircle, XCircle, ExternalLink, KeyRound,
} from 'lucide-react';

interface ClientSettingsTabProps {
    client: any;
    onUpdated: () => void;
    /** Called after a successful Save so the host (e.g. the edit modal) can close. */
    onClose?: () => void;
}

const ClientSettingsTab: React.FC<ClientSettingsTabProps> = ({ client, onUpdated, onClose }) => {
    const navigate = useNavigate();
    const [userRole] = useState(localStorage.getItem('user_role') || 'client');
    const [saving, setSaving] = useState(false);
    const [consultants, setConsultants] = useState<{ id: string; name: string }[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [builderStatus, setBuilderStatus] = useState<{ connected: boolean; validxAppUrl: string | null } | null>(null);
    const [provisioning, setProvisioning] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);

    const [form, setForm] = useState({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        website_url: client.website_url || '',
        consultant_id: client.consultant_id || '',
    });

    useEffect(() => {
        if (userRole === 'super') {
            getAllConsultants().then(res => {
                setConsultants(res.consultants || []);
            }).catch(err => {
                console.error('Error fetching consultants:', err);
            });
        }
    }, [userRole]);

    useEffect(() => {
        getBuilderStatus()
            .then(setBuilderStatus)
            .catch(() => setBuilderStatus({ connected: false, validxAppUrl: null }));
    }, []);

    const handleProvisionBuilder = async () => {
        try {
            setProvisioning(true);
            const data = await requestAgentBridgeUrl();
            toast.success('Builder workspace provisioned successfully!');
            setBuilderStatus({ connected: true, validxAppUrl: data.url ? builderStatus?.validxAppUrl || null : null });
        } catch (err: any) {
            const detail = err?.response?.data?.detail || err.message || 'Failed to provision builder';
            toast.error(detail);
        } finally {
            setProvisioning(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateClient(client.id, form);
            toast.success('Client updated successfully');
            onUpdated();
            onClose?.();
        } catch (err: any) {
            console.error('Error updating client:', err);
            toast.error(err.message || 'Failed to update client');
        } finally {
            setSaving(false);
        }
    };

    const handleSendReset = async () => {
        try {
            setSendingReset(true);
            const res = await sendClientPasswordReset(client.id);
            toast.success(res?.message || 'Password reset link sent');
        } catch (err: any) {
            toast.error(err.message || 'Failed to send reset link');
        } finally {
            setSendingReset(false);
        }
    };

    const handleStatusToggle = async () => {
        const newStatus = (!client.status || client.status === 'active') ? 'inactive' : 'active';
        try {
            await updateClient(client.id, { status: newStatus });
            toast.success(`Client ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
            onUpdated();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update status');
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await deleteClient(client.id);
            toast.success('Client deleted successfully');
            navigate('/consultant/dashboard');
        } catch (err: any) {
            console.error('Error deleting client:', err);
            toast.error(err.message || 'Failed to delete client');
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const isActive = !client.status || client.status === 'active';

    const inputClass = "w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all outline-none";

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column: Form */}
            <div className="lg:col-span-2 space-y-6">
                {/* Client Information */}
                <div className="bg-white rounded-xl border border-gray-200/80 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900">Client Information</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Update your client's contact details and profile.</p>
                    </div>
                    <div className="p-6 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Full Name</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="Client name"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Email Address</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        placeholder="email@company.com"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Phone Number</label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        placeholder="+1 (555) 000-0000"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Website</label>
                                <div className="relative">
                                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        name="website_url"
                                        value={form.website_url}
                                        onChange={handleChange}
                                        placeholder="https://example.com"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        </div>

                        {userRole === 'super' && (
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Assigned Consultant</label>
                                <div className="relative">
                                    <UserCog className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <select
                                        name="consultant_id"
                                        value={form.consultant_id}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all outline-none appearance-none"
                                    >
                                        <option value="">-- Select Consultant --</option>
                                        {consultants.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-1">
                            <button
                                type="button"
                                onClick={handleSendReset}
                                disabled={sendingReset}
                                title="Email a password-reset link to the client's login address"
                                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all disabled:opacity-50 whitespace-nowrap"
                            >
                                {sendingReset ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                                Send reset link
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm whitespace-nowrap"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right column: Status & Danger */}
            <div className="space-y-6">
                {/* Account Status */}
                <div className="bg-white rounded-xl border border-gray-200/80 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900">Account Status</h3>
                    </div>
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-brand-50' : 'bg-gray-100'}`}>
                                {isActive
                                    ? <Shield className="w-5 h-5 text-brand-600" />
                                    : <ShieldOff className="w-5 h-5 text-gray-400" />
                                }
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-900">
                                    {isActive ? 'Active' : 'Inactive'}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {isActive ? 'Chatbot and login enabled' : 'All access suspended'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleStatusToggle}
                            className={`whitespace-nowrap w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                isActive
                                    ? 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                                    : 'bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200'
                            }`}
                        >
                            <Power className="w-4 h-4" />
                            {isActive ? 'Deactivate Account' : 'Activate Account'}
                        </button>
                    </div>
                </div>

                {/* Builder Integration */}
                <div className="bg-white rounded-xl border border-gray-200/80 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <Bot className="w-4 h-4 text-brand-500" />
                            <h3 className="text-sm font-semibold text-gray-900">Agent Builder</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        {builderStatus === null ? (
                            <div className="flex items-center gap-2 py-2">
                                <Loader2 className="w-4 h-4 text-brand-500 animate-spin" />
                                <span className="text-sm text-gray-500">Checking...</span>
                            </div>
                        ) : builderStatus.connected ? (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2.5 p-3 rounded-lg bg-brand-50 border border-brand-200">
                                    <CheckCircle className="w-4 h-4 text-brand-600 shrink-0" />
                                    <span className="text-sm font-medium text-brand-800">Connected</span>
                                </div>
                                <p className="text-xs text-gray-400">
                                    Agent Builder powers AI agents that build and modify software for this client.
                                </p>
                                <button
                                    onClick={async () => {
                                        // Signed bridge — a raw builder URL has no session and
                                        // lands the consultant on the login page. Tab opens
                                        // synchronously: window.open after an await gets
                                        // popup-blocked (reads as a dead click).
                                        const tab = window.open('about:blank', '_blank');
                                        try {
                                            const data = await requestAgentBridgeUrl(
                                                `/customer/workspace?workspaceId=${client.id}`,
                                                client.id,
                                            );
                                            if (data?.url) {
                                                if (tab) tab.location.href = data.url;
                                                else window.location.href = data.url;
                                                return;
                                            }
                                        } catch (e) {
                                            console.warn('[ClientSettingsTab] bridge failed:', e);
                                        }
                                        tab?.close();
                                        toast.error('Could not open the workspace — try again.');
                                    }}
                                    title="Open this client's builder workspace (all projects)"
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Go to Workspace
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-200">
                                    <XCircle className="w-4 h-4 text-gray-400 shrink-0" />
                                    <span className="text-sm text-gray-600">Not connected</span>
                                </div>
                                <p className="text-xs text-gray-500">
                                    Connect to Agent Builder to enable AI-powered workspaces for this client.
                                </p>
                                <button
                                    onClick={handleProvisionBuilder}
                                    disabled={provisioning}
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 whitespace-nowrap"
                                >
                                    {provisioning ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Connecting...
                                        </>
                                    ) : (
                                        <>
                                            <Bot className="w-4 h-4" />
                                            Connect Builder
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl border border-red-200/80 overflow-hidden">
                    <div className="px-6 py-4 border-b border-red-100">
                        <h3 className="text-sm font-semibold text-red-700">Danger Zone</h3>
                    </div>
                    <div className="p-6">
                        <p className="text-sm text-gray-700 mb-1">Delete this client permanently</p>
                        <p className="text-xs text-gray-500 mb-4">All data, history, and agents will be removed. This cannot be undone.</p>

                        {!showDeleteConfirm ? (
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors whitespace-nowrap"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete Client
                            </button>
                        ) : (
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                <div className="flex items-start gap-2.5 mb-3">
                                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                                    <p className="text-xs text-red-700">
                                        Are you sure? This will permanently delete <strong>{client.name}</strong> and all associated data.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleDelete}
                                        disabled={deleting}
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                                    >
                                        {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                        {deleting ? 'Deleting...' : 'Confirm'}
                                    </button>
                                    <button
                                        onClick={() => setShowDeleteConfirm(false)}
                                        className="flex-1 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientSettingsTab;
